import { useCallback, useEffect, useRef } from "react"
import { useTemplateMetadata } from "resources/templates"
import { ERROR } from "helpers/data-statuses"
import { decodeFamilyMetricIds, type CustomGraphFamilyContext } from "../templates/custom/family"

export type FamilyResolutionMap = Record<string, CustomGraphFamilyContext | null>
export type FamilyResolvedHandler = (resolutions: FamilyResolutionMap) => void

interface CustomTemplateFamilyProbeProps {
  templateId: string
  onResolve: (templateId: string, context: CustomGraphFamilyContext | null) => void
}

function CustomTemplateFamilyProbe({ templateId, onResolve }: CustomTemplateFamilyProbeProps) {
  const [templateMetadata, templateMetadataStatus] = useTemplateMetadata(templateId)

  useEffect(() => {
    if (templateMetadataStatus === ERROR) {
      // Settle deliberately into the unresolved bucket rather than hanging indefinitely with no retry.
      onResolve(templateId, null)
      return
    }
    if (!templateMetadata?.config?.metrics) return
    onResolve(templateId, decodeFamilyMetricIds(templateMetadata.config.metrics).context)
  }, [templateId, templateMetadata, templateMetadataStatus, onResolve])

  return null
}

interface CustomTemplateFamilyProbesProps {
  templateIds: string[]
  onResolved: FamilyResolvedHandler
}

// Probes resolve independently as each template's own REST fetch completes. Collecting them into
// pendingRef and flushing on the next tick coalesces any that land close together into one state
// update for the parent, instead of one setState per probe.
export default function CustomTemplateFamilyProbes({ templateIds, onResolved }: CustomTemplateFamilyProbesProps) {
  const pendingRef = useRef<FamilyResolutionMap>({})
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (flushTimerRef.current != null) clearTimeout(flushTimerRef.current)
  }, [])

  const handleResolve = useCallback((templateId: string, context: CustomGraphFamilyContext | null) => {
    pendingRef.current = { ...pendingRef.current, [templateId]: context }
    if (flushTimerRef.current == null) {
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = null
        const batch = pendingRef.current
        pendingRef.current = {}
        onResolved(batch)
      }, 0)
    }
  }, [onResolved])

  return <>
    {templateIds.map(templateId => <CustomTemplateFamilyProbe key={templateId} templateId={templateId} onResolve={handleResolve} />)}
  </>
}
